import React from "react";
import { LoadingState, ErrorState } from "@/components/ui/StatePanel";
import { Button } from "@/components/ui/Button";
import { GATE, isNotFoundError, queryGate } from "@/lib/queryGate";

/**
 * Fix B — the house loading/error gate, in one place. Returns the loading UI while a query is
 * pending, the error UI (calm copy + a Retry that calls refetch) when it failed, or null when the
 * data is ready. Callers keep short-circuit safety: `{queryStateGate({...}) || <Content/>}` only
 * evaluates <Content/> once the gate is null. This is deliberately DUMB — no hooks, no data — so a
 * failed fetch can never render as "empty"; loading, error, and empty stay three distinct states.
 *
 * @param t react-i18next translator (callers own the hook; this is a plain factory). Optional —
 *          pages without i18n fall back to calm English defaults.
 * @param onRetry usually a query's `refetch`. Omitted -> no Retry button.
 */
export function queryStateGate({
  isLoading,
  // GATE-1: the two flags that close the backoff-gap and paused holes. Callers pass whichever they
  // have — a whole react-query result spreads in, and callers that AGGREGATE several queries into
  // one set of booleans (CoachingHubPage, ActionPlanRecordPage) pass the OR of each flag.
  isPending,
  isPaused,
  isError,
  // F47 / C-146 item 2 -- OPTIONAL. Passed, a 404 renders the not-found card with NO retry; omitted,
  // this gate behaves exactly as before, so no existing caller changes meaning.
  error,
  onRetry,
  isRetrying = false,
  t,
  loadingMessage,
  errorTitle,
  errorMessage,
  className,
}) {
  const tr = (key, fallback) => (t ? t(key) : fallback);
  // GATE-1: one shared predicate (@/lib/queryGate) decides; this function only presents. A caller
  // that still passes only isLoading/isError keeps its old behaviour — but it also keeps both holes,
  // which is why the fence test forbids that shape on the teacher path.
  const state = queryGate({ isPending, isLoading, isError, isPaused });
  if (state === GATE.LOADING) {
    return <LoadingState message={loadingMessage} className={className} />;
  }
  // A 404 is still GATE.ERROR for every decision elsewhere; only the CARD differs. Retry is omitted
  // deliberately -- a 404 does not become a 200 on the second attempt, and the button would make a
  // correct refusal look like a transient fault the reader can clear by pressing it.
  if (state === GATE.ERROR && isNotFoundError(error)) {
    return (
      <ErrorState
        className={className}
        title={tr("state.notFoundTitle", "Not found")}
        message={tr(
          "state.notFoundMessage",
          "This page isn't available. If you followed a link, the link may be out of date.",
        )}
      />
    );
  }

  if (state === GATE.ERROR) {
    return (
      <ErrorState
        className={className}
        title={errorTitle || tr("state.loadFailedTitle", "Couldn't load this")}
        message={errorMessage || tr("state.loadFailedMessage", "Something went wrong on our end. Try again in a moment.")}
        action={
          onRetry ? (
            <Button type="button" variant="secondary" size="sm" onClick={() => onRetry()} disabled={isRetrying}>
              {tr("state.retry", "Retry")}
            </Button>
          ) : null
        }
      />
    );
  }
  return null;
}

export default queryStateGate;
